import React, { useState, useEffect } from 'react';
import axios from 'axios';

const EditGiveaway = ({ giveaway, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: giveaway.title || '',
    description: giveaway.description || '',
    prize: giveaway.prize || '',
    endDate: giveaway.endDate ? new Date(giveaway.endDate).toISOString().slice(0, 16) : '',
    specialThanksName: giveaway.specialThanks?.name || '',
    specialThanksMessage: giveaway.specialThanks?.message || ''
  });
  const [banner, setBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(giveaway.banner?.url || null);
  const [requirements, setRequirements] = useState(giveaway.requirements || []);
  const [numberOfWinners, setNumberOfWinners] = useState(giveaway.numberOfWinners || 1);
  const [autoPayoutEnabled, setAutoPayoutEnabled] = useState(giveaway.autoPayoutEnabled || false);
  const [autoRollEnabled, setAutoRollEnabled] = useState(giveaway.autoRollEnabled || false);
  const [gameKeys, setGameKeys] = useState([]);
  const [rerollTimerHours, setRerollTimerHours] = useState(giveaway.rerollTimerHours || 24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Initialize game keys from existing giveaway
    if (giveaway.gameKeys && giveaway.gameKeys.length > 0) {
      // Can't show encrypted keys, so initialize empty for user to re-enter
      const keys = [];
      for (let i = 0; i < (giveaway.numberOfWinners || 1); i++) {
        keys.push({ position: i + 1, key: '' });
      }
      setGameKeys(keys);
    } else {
      const keys = [];
      for (let i = 0; i < numberOfWinners; i++) {
        keys.push({ position: i + 1, key: '' });
      }
      setGameKeys(keys);
    }
  }, [giveaway, numberOfWinners]);

  const requirementTypes = [
    { value: 'twitter_follow', label: '🐦 Follow on Twitter/X' },
    { value: 'twitter_like', label: '❤️ Like Twitter/X Post' },
    { value: 'twitter_retweet', label: '🔄 Retweet Twitter/X Post' },
    { value: 'twitter_comment', label: '💬 Comment on Twitter/X Post' },
    { value: 'instagram_follow', label: '📸 Follow on Instagram' },
    { value: 'instagram_like', label: '❤️ Like Instagram Post' },
    { value: 'instagram_comment', label: '💬 Comment on Instagram Post' },
    { value: 'youtube_subscribe', label: '▶️ Subscribe on YouTube' },
    { value: 'youtube_like', label: '👍 Like YouTube Video' },
    { value: 'youtube_comment', label: '💬 Comment on YouTube Video' },
    { value: 'twitch_follow', label: '🎮 Follow on Twitch' },
    { value: 'twitch_subscribe', label: '⭐ Subscribe on Twitch' },
    { value: 'kick_follow', label: '🦵 Follow on Kick' },
    { value: 'tiktok_follow', label: '🎵 Follow on TikTok' },
    { value: 'tiktok_like', label: '❤️ Like TikTok Video' },
    { value: 'facebook_like', label: '👍 Like Facebook Page' },
    { value: 'facebook_share', label: '🔄 Share Facebook Post' },
    { value: 'discord_join', label: '💬 Join Discord Server' },
    { value: 'reddit_join', label: '🔴 Join Subreddit' },
    { value: 'reddit_upvote', label: '⬆️ Upvote Reddit Post' },
    { value: 'steam_follow', label: '🎮 Follow on Steam' },
    { value: 'steam_wishlist', label: '❤️ Wishlist on Steam' },
    { value: 'custom_url', label: '🔗 Visit Custom URL' }
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Banner image must be less than 10MB');
        return;
      }
      setBanner(file);
      setBannerPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  // Helper function to sanitize and normalize URLs
  const sanitizeUrl = (url) => {
    if (!url || !url.trim()) return '';
    
    let cleanUrl = url.trim();
    
    // Convert HTTP:// to https:// (case insensitive)
    cleanUrl = cleanUrl.replace(/^HTTP:\/\//i, 'https://');
    
    // If URL doesn't have a protocol, add https://
    if (!cleanUrl.match(/^https?:\/\//i)) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    // Normalize the URL to lowercase (except for case-sensitive path/query parts)
    try {
      const urlObj = new URL(cleanUrl);
      // Convert protocol and hostname to lowercase
      urlObj.protocol = urlObj.protocol.toLowerCase();
      urlObj.hostname = urlObj.hostname.toLowerCase();
      cleanUrl = urlObj.toString();
    } catch (e) {
      // If URL is invalid, just return the cleaned version
      console.warn('Invalid URL format:', cleanUrl);
    }
    
    return cleanUrl;
  };

  const generateDescription = (type, input) => {
    if (!input) return '';
    const cleanInput = input.trim();
    
    switch (type) {
      case 'twitter_follow': return `Follow ${cleanInput.startsWith('@') ? cleanInput : '@' + cleanInput} on Twitter/X`;
      case 'twitter_like': return `Like this post on Twitter/X`;
      case 'twitter_retweet': return `Retweet this post on Twitter/X`;
      case 'twitter_comment': return `Comment on this post on Twitter/X`;
      case 'instagram_follow': return `Follow ${cleanInput.startsWith('@') ? cleanInput : '@' + cleanInput} on Instagram`;
      case 'instagram_like': return `Like this post on Instagram`;
      case 'instagram_comment': return `Comment on this post on Instagram`;
      case 'youtube_subscribe': return `Subscribe to ${cleanInput} on YouTube`;
      case 'youtube_like': return `Like this video on YouTube`;
      case 'youtube_comment': return `Comment on this video on YouTube`;
      case 'twitch_follow': return `Follow ${cleanInput} on Twitch`;
      case 'twitch_subscribe': return `Subscribe to ${cleanInput} on Twitch`;
      case 'kick_follow': return `Follow ${cleanInput} on Kick`;
      case 'tiktok_follow': return `Follow ${cleanInput.startsWith('@') ? cleanInput : '@' + cleanInput} on TikTok`;
      case 'tiktok_like': return `Like this video on TikTok`;
      case 'facebook_like': return `Like ${cleanInput} on Facebook`;
      case 'facebook_share': return `Share this post on Facebook`;
      case 'discord_join': return `Join ${cleanInput} Discord Server`;
      case 'reddit_join': return `Join ${cleanInput.startsWith('r/') ? cleanInput : 'r/' + cleanInput} subreddit`;
      case 'reddit_upvote': return `Upvote this post on Reddit`;
      case 'steam_follow': return `Follow ${cleanInput} on Steam`;
      case 'steam_wishlist': return `Wishlist ${cleanInput} on Steam`;
      case 'custom_url': return `Visit: ${cleanInput}`;
      default: return cleanInput;
    }
  };

  // Helper function to auto-generate URL based on type and input
  const generateUrl = (type, input) => {
    if (!input) return '';
    
    const cleanInput = input.trim().replace(/^@/, ''); // Remove @ prefix if present
    
    switch (type) {
      case 'twitter_follow':
        return `https://twitter.com/${cleanInput}`;
      case 'instagram_follow':
        return `https://instagram.com/${cleanInput}`;
      case 'youtube_subscribe':
        // YouTube channels can be either @username or channel name
        if (input.trim().startsWith('@')) {
          return `https://youtube.com/${input.trim()}`;
        }
        return `https://youtube.com/@${cleanInput}`;
      case 'twitch_follow':
      case 'twitch_subscribe':
        return `https://twitch.tv/${cleanInput}`;
      case 'kick_follow':
        return `https://kick.com/${cleanInput}`;
      case 'tiktok_follow':
        return `https://tiktok.com/@${cleanInput}`;
      case 'facebook_like':
        return `https://facebook.com/${cleanInput}`;
      case 'reddit_join':
        // Handle if user types 'r/subreddit' or just 'subreddit'
        const subreddit = cleanInput.replace(/^r\//, '');
        return `https://reddit.com/r/${subreddit}`;
      default:
        return '';
    }
  };

  const addRequirement = () => {
    setRequirements([
      ...requirements,
      { type: 'twitter_follow', description: '', url: '', required: true, userInput: '' }
    ]);
  };

  const updateRequirement = (index, field, value) => {
    const updated = [...requirements];
    
    // Sanitize URL field
    if (field === 'url') {
      updated[index][field] = sanitizeUrl(value);
    } else {
      updated[index][field] = value;
    }
    
    // Auto-generate description and URL when userInput changes
    if (field === 'userInput') {
      updated[index].description = generateDescription(updated[index].type, value);
      // Auto-generate URL for follow-type requirements
      const autoUrlTypes = ['twitter_follow', 'instagram_follow', 'youtube_subscribe', 
                           'twitch_follow', 'twitch_subscribe', 'kick_follow', 'tiktok_follow',
                           'facebook_like', 'reddit_join'];
      if (autoUrlTypes.includes(updated[index].type)) {
        updated[index].url = generateUrl(updated[index].type, value);
      }
    }
    
    // Auto-generate description and URL when type changes
    if (field === 'type') {
      updated[index].description = generateDescription(value, updated[index].userInput);
      // Auto-generate URL for follow-type requirements if userInput exists
      const autoUrlTypes = ['twitter_follow', 'instagram_follow', 'youtube_subscribe', 
                           'twitch_follow', 'twitch_subscribe', 'kick_follow', 'tiktok_follow',
                           'facebook_like', 'reddit_join'];
      if (autoUrlTypes.includes(value) && updated[index].userInput) {
        updated[index].url = generateUrl(value, updated[index].userInput);
      }
    }
    
    setRequirements(updated);
  };

  const removeRequirement = (index) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleNumberOfWinnersChange = (value) => {
    const num = parseInt(value) || 1;
    setNumberOfWinners(num);
    
    if (autoPayoutEnabled) {
      const newGameKeys = [];
      for (let i = 0; i < num; i++) {
        newGameKeys.push({
          position: i + 1,
          key: gameKeys[i]?.key || ''
        });
      }
      setGameKeys(newGameKeys);
    }
  };

  const updateGameKey = (position, value) => {
    const updatedKeys = [...gameKeys];
    const index = updatedKeys.findIndex(k => k.position === position);
    if (index !== -1) {
      updatedKeys[index].key = value;
    }
    setGameKeys(updatedKeys);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.title || !formData.description || !formData.prize || !formData.endDate) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      if (new Date(formData.endDate) <= new Date()) {
        setError('End date must be in the future');
        setLoading(false);
        return;
      }

      if (numberOfWinners < 1 || numberOfWinners > 100) {
        setError('Number of winners must be between 1 and 100');
        setLoading(false);
        return;
      }

      if (requirements.length === 0) {
        setError('Please add at least one requirement');
        setLoading(false);
        return;
      }

      if (autoPayoutEnabled) {
        const validKeys = gameKeys.filter(k => k.key && k.key.trim());
        if (validKeys.length < numberOfWinners) {
          setError(`You must provide ${numberOfWinners} game key${numberOfWinners > 1 ? 's' : ''} for ${numberOfWinners} winner${numberOfWinners > 1 ? 's' : ''}`);
          setLoading(false);
          return;
        }
        
        for (let i = 0; i < numberOfWinners; i++) {
          if (!gameKeys[i] || !gameKeys[i].key || !gameKeys[i].key.trim()) {
            setError(`Game key for winner ${i + 1} is required`);
            setLoading(false);
            return;
          }
        }
        
        if (!rerollTimerHours || rerollTimerHours < 1 || rerollTimerHours > 168) {
          setError('Reroll timer must be between 1 and 168 hours');
          setLoading(false);
          return;
        }
      }

      const fixedRequirements = requirements.map(req => {
        let cleanedReq = { ...req };
        delete cleanedReq.userInput;
        
        if (cleanedReq.url && !cleanedReq.url.startsWith('http://') && !cleanedReq.url.startsWith('https://')) {
          if (cleanedReq.url.includes('.')) {
            cleanedReq.url = `https://${cleanedReq.url}`;
          }
        }
        return cleanedReq;
      });

      const token = localStorage.getItem('token');
      const data = new FormData();
      
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('prize', formData.prize);
      data.append('endDate', formData.endDate);
      data.append('requirements', JSON.stringify(fixedRequirements));
      data.append('numberOfWinners', numberOfWinners.toString());

      if (formData.specialThanksName || formData.specialThanksMessage) {
        data.append('specialThanks', JSON.stringify({
          name: formData.specialThanksName,
          message: formData.specialThanksMessage
        }));
      }

      if (banner) {
        data.append('banner', banner);
      }

      if (autoPayoutEnabled) {
        data.append('autoPayoutEnabled', 'true');
        data.append('gameKeys', JSON.stringify(gameKeys.slice(0, numberOfWinners)));
        data.append('gameKey', gameKeys[0]?.key || '');
        data.append('rerollTimerHours', rerollTimerHours.toString());
      } else {
        data.append('autoPayoutEnabled', 'false');
      }

      if (autoRollEnabled) {
        data.append('autoRollEnabled', 'true');
      } else {
        data.append('autoRollEnabled', 'false');
      }

      const response = await axios.put(`/api/giveaways/${giveaway._id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess(response.data.giveaway);
    } catch (err) {
      console.error('Update giveaway error:', err);
      setError(err.response?.data?.message || 'Failed to update giveaway');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <h2 className="modal-title">✏️ Edit Giveaway</h2>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              maxLength={200}
              placeholder="e.g., Steam Deck Giveaway"
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              maxLength={2000}
              placeholder="Describe your giveaway..."
              rows={4}
              className="form-textarea"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Banner Image (Optional)</label>
            {bannerPreview && (
              <div className="banner-preview">
                <img src={bannerPreview} alt="Banner preview" />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleBannerChange}
              className="file-input"
            />
            <p className="form-hint">Max size: 10MB. Upload new image to replace current banner.</p>
          </div>

          <div className="form-group">
            <label className="form-label">Prize *</label>
            <input
              type="text"
              name="prize"
              value={formData.prize}
              onChange={handleInputChange}
              maxLength={500}
              placeholder="e.g., Steam Deck 512GB + $50 Steam Gift Card"
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">End Date & Time *</label>
            <input
              type="datetime-local"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Number of Winners * 🏆</label>
            <input
              type="number"
              value={numberOfWinners}
              onChange={(e) => handleNumberOfWinnersChange(e.target.value)}
              min="1"
              max="100"
              placeholder="1"
              className="form-input"
              required
            />
            <p className="form-hint">How many winners will be selected (1-100)</p>
          </div>

          <div className="special-thanks-section">
            <h4>💝 Special Thanks (Optional)</h4>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.9rem' }}>Name</label>
              <input
                type="text"
                name="specialThanksName"
                value={formData.specialThanksName}
                onChange={handleInputChange}
                placeholder="Person or organization to thank"
                className="form-input"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.9rem' }}>Message</label>
              <textarea
                name="specialThanksMessage"
                value={formData.specialThanksMessage}
                onChange={handleInputChange}
                placeholder="Your thank you message..."
                rows={3}
                className="form-textarea"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
          </div>

          <div className="form-group">
            <div className="requirements-header">
              <h4>📋 Entry Requirements *</h4>
              <button
                type="button"
                onClick={addRequirement}
                className="add-requirement-btn"
              >
                + Add Requirement
              </button>
            </div>

            {requirements.map((req, index) => (
              <div key={index} className="requirement-card">
                <div className="requirement-header">
                  <label className="requirement-label">Requirement #{index + 1}</label>
                  <button
                    type="button"
                    onClick={() => removeRequirement(index)}
                    className="remove-requirement-btn"
                  >
                    ✕
                  </button>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.9rem' }}>Type</label>
                  <select
                    value={req.type}
                    onChange={(e) => updateRequirement(index, 'type', e.target.value)}
                    className="form-input"
                    style={{ fontSize: '0.95rem' }}
                  >
                    {requirementTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.9rem' }}>Description</label>
                  <input
                    type="text"
                    value={req.description}
                    onChange={(e) => updateRequirement(index, 'description', e.target.value)}
                    placeholder="Requirement description"
                    className="form-input"
                    style={{ fontSize: '0.95rem' }}
                  />
                </div>

                {['twitter_like', 'twitter_retweet', 'twitter_comment', 'instagram_like', 'instagram_comment', 'youtube_like', 'youtube_comment', 'tiktok_like', 'facebook_share', 'reddit_upvote', 'steam_wishlist', 'custom_url'].includes(req.type) && (
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.9rem' }}>URL *</label>
                    <input
                      type="text"
                      value={req.url || ''}
                      onChange={(e) => updateRequirement(index, 'url', e.target.value)}
                      placeholder="https://..."
                      className="form-input"
                      style={{ fontSize: '0.95rem' }}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={req.required}
                    onChange={(e) => updateRequirement(index, 'required', e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  <label style={{ fontSize: '0.85rem' }}>Required</label>
                </div>
              </div>
            ))}

            {requirements.length === 0 && (
              <p className="requirements-empty">
                No requirements added yet. Click "Add Requirement" to get started.
              </p>
            )}
          </div>

          <div className="special-thanks-section" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            padding: '20px', 
            borderRadius: '12px',
            border: '2px solid #764ba2'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <input
                type="checkbox"
                id="autoPayoutToggle"
                checked={autoPayoutEnabled}
                onChange={(e) => setAutoPayoutEnabled(e.target.checked)}
                style={{ marginRight: '10px', width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <label htmlFor="autoPayoutToggle" style={{ 
                fontSize: '1.1rem', 
                fontWeight: 'bold', 
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🤖 Enable Auto-Payout
              </label>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#e0e0e0', marginBottom: '15px', lineHeight: '1.5' }}>
              Automatically distribute game keys when winners claim them via DM. Keys are sent by KeyDistributor bot.
            </p>

            {autoPayoutEnabled && (
              <>
                <div className="form-group">
                  <label className="form-label" style={{ color: 'white', fontSize: '0.9rem', marginBottom: '10px' }}>
                    Game Keys * 🔐
                  </label>
                  <small style={{ color: '#ffd700', fontSize: '0.75rem', display: 'block', marginBottom: '15px' }}>
                    ⚠️ Re-enter all game keys. They will be encrypted and stored securely.
                  </small>
                  
                  {gameKeys.slice(0, numberOfWinners).map((keyData, index) => (
                    <div key={index} style={{ marginBottom: '15px' }}>
                      <label style={{ color: 'white', fontSize: '0.85rem', display: 'block', marginBottom: '5px' }}>
                        {numberOfWinners > 1 ? `Winner #${index + 1} Key` : 'Game Key'}
                      </label>
                      <input
                        type="text"
                        value={keyData.key}
                        onChange={(e) => updateGameKey(index + 1, e.target.value)}
                        placeholder="XXXXX-XXXXX-XXXXX"
                        className="form-input"
                        style={{ fontSize: '0.95rem', fontFamily: 'monospace' }}
                        required={autoPayoutEnabled}
                      />
                    </div>
                  ))}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ color: 'white', fontSize: '0.9rem' }}>
                    Reroll Timer (Hours) * ⏱️
                  </label>
                  <input
                    type="number"
                    value={rerollTimerHours}
                    onChange={(e) => setRerollTimerHours(parseInt(e.target.value))}
                    placeholder="24"
                    min="1"
                    max="168"
                    className="form-input"
                    style={{ fontSize: '0.95rem' }}
                    required={autoPayoutEnabled}
                  />
                  <small style={{ color: '#e0e0e0', fontSize: '0.75rem' }}>
                    How long each winner has to claim before auto-reroll (1-168 hours)
                  </small>
                </div>
              </>
            )}
          </div>

          <div className="special-thanks-section" style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
            padding: '20px', 
            borderRadius: '12px',
            border: '2px solid #f5576c'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              <input
                type="checkbox"
                id="autoRollToggle"
                checked={autoRollEnabled}
                onChange={(e) => setAutoRollEnabled(e.target.checked)}
                style={{ marginRight: '10px', width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <label htmlFor="autoRollToggle" style={{ 
                fontSize: '1.1rem', 
                fontWeight: 'bold', 
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🎲 Enable Auto-Roll
              </label>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#fff', marginBottom: '0', lineHeight: '1.5' }}>
              Automatically roll for winners when the giveaway ends. No manual intervention required.
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Updating...' : 'Update Giveaway'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGiveaway;
